/**
 * events.ts - Random Event Definitions
 *
 * 30 random events organized into 3 tiers.
 * Each event has conditions, choices, and pure effect functions.
 */

import type { GameStateData } from '../state.js';

// ── Types ──────────────────────────────────────────────────

export interface EventChoice {
  labelKey: string;
  effect: (state: GameStateData) => Partial<GameStateData>;
  resultKey: string;
}

/** Defense system: auto-resolve negative events when prepared */
export interface EventDefense {
  /** Returns true when the player has the right staff/facilities to defend */
  condition: (state: GameStateData) => boolean;
  /** i18n key for the auto-resolve success message */
  defenseKey: string;
  /** i18n key for hint shown when defense condition is NOT met */
  hintKey: string;
  /** Reduced or neutralized effect applied on successful defense */
  reducedEffect?: (state: GameStateData) => Partial<GameStateData>;
  /** Bonus SP awarded on successful defense */
  bonusReward?: number;
}

export interface GameEvent {
  id: string;
  tier: 1 | 2 | 3;
  nameKey: string;
  descKey: string;
  condition: (state: GameStateData) => boolean;
  choices: EventChoice[];
  /** Display time in ms. 0 = wait for player choice */
  duration: number;
  /** Choice index to auto-resolve when time expires */
  autoResolve?: number;
  /** Optional defense system — auto-resolves when prepared */
  defense?: EventDefense;
}

// ── Helper: clamp ──────────────────────────────────────────

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

// ── Tier 1: Early (queue 3+, any time) ─────────────────────

const tier1Events: GameEvent[] = [
  {
    id: 'rain',
    tier: 1,
    nameKey: 'evt_rain_name',
    descKey: 'evt_rain_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 15000,
    autoResolve: 0,
    choices: [
      {
        labelKey: 'evt_rain_c1',
        effect: (s) => {
          const q = [...(s.lobbyWaitQueue || [])];
          const removed = Math.min(3, q.length);
          return { lobbyWaitQueue: q.slice(removed) };
        },
        resultKey: 'evt_rain_r1',
      },
      {
        labelKey: 'evt_rain_c2',
        effect: (s) => ({ sp: Math.max(0, s.sp - 100) }),
        resultKey: 'evt_rain_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.lobbyBuilding || []).some(
          (f) => f.facilityType === 'poster_wall' && f.facilityLevel >= 3,
        ),
      defenseKey: 'evt_rain_defense',
      hintKey: 'evt_rain_hint',
      reducedEffect: () => ({}),
      bonusReward: 0,
    },
  },
  {
    id: 'couple_fight',
    tier: 1,
    nameKey: 'evt_couple_fight_name',
    descKey: 'evt_couple_fight_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 10000,
    autoResolve: 0,
    choices: [
      {
        labelKey: 'evt_couple_fight_c1',
        effect: (s) => {
          const hasSecurity = (s.ghostStaff || []).some(
            (st) => st.type === 'security',
          );
          if (hasSecurity) return {};
          const q = (s.lobbyWaitQueue || []).map((v) => ({
            ...v,
            patience: Math.max(0, v.patience - 20),
          }));
          return { lobbyWaitQueue: q };
        },
        resultKey: 'evt_couple_fight_r1',
      },
    ],
    defense: {
      condition: (s) =>
        (s.ghostStaff || []).some((st) => st.type === 'security'),
      defenseKey: 'evt_couple_fight_defense',
      hintKey: 'evt_couple_fight_hint',
      reducedEffect: () => ({}),
      bonusReward: 0,
    },
  },
  {
    id: 'crying_child',
    tier: 1,
    nameKey: 'evt_crying_child_name',
    descKey: 'evt_crying_child_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 10000,
    autoResolve: 0,
    choices: [
      {
        labelKey: 'evt_crying_child_c1',
        effect: (s) => ({ sp: Math.max(0, s.sp - 50) }),
        resultKey: 'evt_crying_child_r1',
      },
      {
        labelKey: 'evt_crying_child_c2',
        effect: (s) => {
          const q = (s.lobbyWaitQueue || []).map((v) => ({
            ...v,
            patience: Math.max(0, v.patience - 10),
          }));
          return { lobbyWaitQueue: q };
        },
        resultKey: 'evt_crying_child_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.lobbyBuilding || []).some((f) => f.facilityType === 'snack_cart'),
      defenseKey: 'evt_crying_child_defense',
      hintKey: 'evt_crying_child_hint',
      reducedEffect: () => ({}),
      bonusReward: 0,
    },
  },
  {
    id: 'blackout',
    tier: 1,
    nameKey: 'evt_blackout_name',
    descKey: 'evt_blackout_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 15000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_blackout_c1',
        effect: (s) => ({ sp: Math.max(0, s.sp - 200) }),
        resultKey: 'evt_blackout_r1',
      },
      {
        labelKey: 'evt_blackout_c2',
        effect: () => ({}),
        resultKey: 'evt_blackout_r2',
      },
    ],
    defense: {
      condition: (s) => {
        const totalLv = (s.lobbyBuilding || []).reduce(
          (sum, f) => sum + (f.facilityLevel || 0),
          0,
        );
        return totalLv >= 10;
      },
      defenseKey: 'evt_blackout_defense',
      hintKey: 'evt_blackout_hint',
      reducedEffect: () => ({}),
      bonusReward: 0,
    },
  },
  {
    id: 'line_cutter',
    tier: 1,
    nameKey: 'evt_line_cutter_name',
    descKey: 'evt_line_cutter_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 4,
    duration: 10000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_line_cutter_c1',
        effect: (s) => {
          const hasSec = (s.ghostStaff || []).some(
            (st) => st.type === 'security',
          );
          return hasSec
            ? { reputation: clamp((s.reputation || 50) + 3, 0, 100) }
            : {};
        },
        resultKey: 'evt_line_cutter_r1',
      },
      {
        labelKey: 'evt_line_cutter_c2',
        effect: (s) => {
          const q = (s.lobbyWaitQueue || []).map((v) => ({
            ...v,
            patience: Math.max(0, v.patience - 15),
          }));
          return { lobbyWaitQueue: q };
        },
        resultKey: 'evt_line_cutter_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.ghostStaff || []).some((st) => st.type === 'security'),
      defenseKey: 'evt_line_cutter_defense',
      hintKey: 'evt_line_cutter_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 3, 0, 100),
      }),
      bonusReward: 0,
    },
  },
  {
    id: 'fake_ghost',
    tier: 1,
    nameKey: 'evt_fake_ghost_name',
    descKey: 'evt_fake_ghost_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_fake_ghost_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 3, 0, 100),
        }),
        resultKey: 'evt_fake_ghost_r1',
      },
    ],
    defense: {
      condition: (s) => (s.lobbyNpcGhosts || []).length >= 1,
      defenseKey: 'evt_fake_ghost_defense',
      hintKey: 'evt_fake_ghost_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 20, 0, 100),
      }),
      bonusReward: 0,
    },
  },
  {
    id: 'lost_item',
    tier: 1,
    nameKey: 'evt_lost_item_name',
    descKey: 'evt_lost_item_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 15000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_lost_item_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 5, 0, 100),
        }),
        resultKey: 'evt_lost_item_r1',
      },
      {
        labelKey: 'evt_lost_item_c2',
        effect: () => ({}),
        resultKey: 'evt_lost_item_r2',
      },
    ],
    defense: {
      condition: (s) => (s.ghostStaff || []).length >= 3,
      defenseKey: 'evt_lost_item_defense',
      hintKey: 'evt_lost_item_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 5, 0, 100),
      }),
      bonusReward: 0,
    },
  },
  {
    id: 'snack_sold_out',
    tier: 1,
    nameKey: 'evt_snack_sold_out_name',
    descKey: 'evt_snack_sold_out_desc',
    condition: (s) =>
      (s.lobbyWaitQueue || []).length >= 3 &&
      (s.lobbyBuilding || []).some((f) => f.facilityType === 'snack_cart'),
    duration: 10000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_snack_sold_out_c1',
        effect: (s) => ({ sp: Math.max(0, s.sp - 100) }),
        resultKey: 'evt_snack_sold_out_r1',
      },
      {
        labelKey: 'evt_snack_sold_out_c2',
        effect: () => ({}),
        resultKey: 'evt_snack_sold_out_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.lobbyBuilding || []).some(
          (f) => f.facilityType === 'snack_cart' && f.facilityLevel >= 2,
        ),
      defenseKey: 'evt_snack_sold_out_defense',
      hintKey: 'evt_snack_sold_out_hint',
      reducedEffect: () => ({}),
      bonusReward: 0,
    },
  },
  {
    id: 'sns_selfie',
    tier: 1,
    nameKey: 'evt_sns_selfie_name',
    descKey: 'evt_sns_selfie_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_sns_selfie_c1',
        effect: (s) => {
          const hasPhotoZone = (s.lobbyBuilding || []).some(
            (f) => f.facilityType === 'photo_zone',
          );
          return hasPhotoZone
            ? { reputation: clamp((s.reputation || 50) + 3, 0, 100) }
            : {};
        },
        resultKey: 'evt_sns_selfie_r1',
      },
    ],
    defense: {
      condition: (s) =>
        (s.lobbyBuilding || []).some((f) => f.facilityType === 'photo_zone'),
      defenseKey: 'evt_sns_selfie_defense',
      hintKey: 'evt_sns_selfie_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 5, 0, 100),
      }),
      bonusReward: 0,
    },
  },
  {
    id: 'hot_weather',
    tier: 1,
    nameKey: 'evt_hot_weather_name',
    descKey: 'evt_hot_weather_desc',
    condition: (s) => (s.lobbyWaitQueue || []).length >= 3,
    duration: 15000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_hot_weather_c1',
        effect: (s) => ({
          sp: Math.max(0, s.sp - 150),
        }),
        resultKey: 'evt_hot_weather_r1',
      },
      {
        labelKey: 'evt_hot_weather_c2',
        effect: (s) => {
          const q = (s.lobbyWaitQueue || []).map((v) => ({
            ...v,
            patience: Math.max(0, v.patience - 10),
          }));
          return { lobbyWaitQueue: q };
        },
        resultKey: 'evt_hot_weather_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.lobbyBuilding || []).some((f) => f.facilityType === 'tv_monitor'),
      defenseKey: 'evt_hot_weather_defense',
      hintKey: 'evt_hot_weather_hint',
      reducedEffect: () => ({}),
      bonusReward: 0,
    },
  },
];

// ── Tier 2: Mid-game (rep 30+, queue 8+) ───────────────────

const tier2Events: GameEvent[] = [
  {
    id: 'tour_bus',
    tier: 2,
    nameKey: 'evt_tour_bus_name',
    descKey: 'evt_tour_bus_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_tour_bus_c1',
        effect: (s) => {
          const q = [...(s.lobbyWaitQueue || [])];
          const maxQ = s.maxQueueSize || 10;
          const toAdd = Math.min(10, maxQ - q.length);
          for (let i = 0; i < toAdd; i++) {
            q.push({
              id: `qv-evt-${Date.now()}-${i}`,
              type: 'couple' as any,
              patience: 50,
              maxPatience: 50,
              arrivalTime: Date.now(),
              isVip: false,
            });
          }
          return { lobbyWaitQueue: q };
        },
        resultKey: 'evt_tour_bus_r1',
      },
    ],
    defense: {
      condition: (s) => (s.maxQueueSize || 10) >= 15,
      defenseKey: 'evt_tour_bus_defense',
      hintKey: 'evt_tour_bus_hint',
      reducedEffect: (s) => {
        // Accept all visitors + bonus
        const q = [...(s.lobbyWaitQueue || [])];
        const maxQ = s.maxQueueSize || 10;
        const toAdd = Math.min(10, maxQ - q.length);
        for (let i = 0; i < toAdd; i++) {
          q.push({
            id: `qv-evt-${Date.now()}-${i}`,
            type: 'couple' as any,
            patience: 50,
            maxPatience: 50,
            arrivalTime: Date.now(),
            isVip: false,
          });
        }
        return { lobbyWaitQueue: q };
      },
      bonusReward: 200,
    },
  },
  {
    id: 'tv_coverage',
    tier: 2,
    nameKey: 'evt_tv_coverage_name',
    descKey: 'evt_tv_coverage_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 15000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_tv_coverage_c1',
        effect: (s) => {
          const success = Math.random() > 0.3;
          return success
            ? { reputation: clamp((s.reputation || 50) + 20, 0, 100) }
            : { reputation: clamp((s.reputation || 50) - 10, 0, 100) };
        },
        resultKey: 'evt_tv_coverage_r1',
      },
      {
        labelKey: 'evt_tv_coverage_c2',
        effect: () => ({}),
        resultKey: 'evt_tv_coverage_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.reputation || 50) >= 50 && s.ghosts.length >= 5,
      defenseKey: 'evt_tv_coverage_defense',
      hintKey: 'evt_tv_coverage_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 20, 0, 100),
      }),
      bonusReward: 0,
    },
  },
  {
    id: 'spy',
    tier: 2,
    nameKey: 'evt_spy_name',
    descKey: 'evt_spy_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 15000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_spy_c1',
        effect: (s) => {
          const hasSec = (s.ghostStaff || []).some(
            (st) => st.type === 'security',
          );
          return hasSec
            ? { sp: s.sp + 500 }
            : {};
        },
        resultKey: 'evt_spy_r1',
      },
      {
        labelKey: 'evt_spy_c2',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) - 5, 0, 100),
        }),
        resultKey: 'evt_spy_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.ghostStaff || []).some((st) => st.type === 'security'),
      defenseKey: 'evt_spy_defense',
      hintKey: 'evt_spy_hint',
      reducedEffect: (s) => ({ sp: s.sp + 500 }),
      bonusReward: 0,
    },
  },
  {
    id: 'health_inspection',
    tier: 2,
    nameKey: 'evt_health_inspection_name',
    descKey: 'evt_health_inspection_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_health_inspection_c1',
        effect: (s) => {
          const dirtyRoom = s.building.some(
            (f) => f.room && f.room.hygiene < 40,
          );
          return dirtyRoom
            ? { sp: Math.max(0, s.sp - 300) }
            : { reputation: clamp((s.reputation || 50) + 10, 0, 100) };
        },
        resultKey: 'evt_health_inspection_r1',
      },
    ],
    defense: {
      condition: (s) => {
        const hasJanitor = (s.ghostStaff || []).some(
          (st) => st.type === 'janitor',
        );
        const rooms = s.building.filter((f) => f.room !== null);
        if (rooms.length === 0) return hasJanitor;
        const avgHygiene =
          rooms.reduce((sum, f) => sum + (f.room?.hygiene || 0), 0) /
          rooms.length;
        return hasJanitor && avgHygiene >= 70;
      },
      defenseKey: 'evt_health_inspection_defense',
      hintKey: 'evt_health_inspection_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 10, 0, 100),
      }),
      bonusReward: 0,
    },
  },
  {
    id: 'halloween_season',
    tier: 2,
    nameKey: 'evt_halloween_season_name',
    descKey: 'evt_halloween_season_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_halloween_season_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 10, 0, 100),
          sp: s.sp + 300,
        }),
        resultKey: 'evt_halloween_season_r1',
      },
    ],
  },
  {
    id: 'ghost_strike',
    tier: 2,
    nameKey: 'evt_ghost_strike_name',
    descKey: 'evt_ghost_strike_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      s.ghosts.some((g) => g.fatigue > 70),
    duration: 15000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_ghost_strike_c1',
        effect: (s) => ({
          sp: Math.max(0, s.sp - 500),
          ghosts: s.ghosts.map((g) =>
            g.fatigue > 70
              ? { ...g, fatigue: Math.max(0, g.fatigue - 30) }
              : g,
          ),
        }),
        resultKey: 'evt_ghost_strike_r1',
      },
      {
        labelKey: 'evt_ghost_strike_c2',
        effect: () => ({}),
        resultKey: 'evt_ghost_strike_r2',
      },
    ],
    defense: {
      condition: (s) => {
        const avgFatigue =
          s.ghosts.length > 0
            ? s.ghosts.reduce((sum, g) => sum + g.fatigue, 0) / s.ghosts.length
            : 0;
        return avgFatigue <= 30;
      },
      defenseKey: 'evt_ghost_strike_defense',
      hintKey: 'evt_ghost_strike_hint',
      reducedEffect: () => ({}),
      bonusReward: 0,
    },
  },
  {
    id: 'mystery_visitor',
    tier: 2,
    nameKey: 'evt_mystery_visitor_name',
    descKey: 'evt_mystery_visitor_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_mystery_visitor_c1',
        effect: (s) => {
          const good = (s.reputation || 50) >= 50;
          return good
            ? { reputation: clamp((s.reputation || 50) + 15, 0, 100) }
            : { reputation: clamp((s.reputation || 50) - 15, 0, 100) };
        },
        resultKey: 'evt_mystery_visitor_r1',
      },
    ],
    defense: {
      condition: (s) => {
        // Average visitor satisfaction >= 60 approximated by reputation
        return (s.reputation || 50) >= 60;
      },
      defenseKey: 'evt_mystery_visitor_defense',
      hintKey: 'evt_mystery_visitor_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 15, 0, 100),
      }),
      bonusReward: 0,
    },
  },
  {
    id: 'maintenance_accident',
    tier: 2,
    nameKey: 'evt_maintenance_accident_name',
    descKey: 'evt_maintenance_accident_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      s.building.some((f) => f.room !== null),
    duration: 0,
    choices: [
      {
        labelKey: 'evt_maintenance_accident_c1',
        effect: (s) => {
          const roomFloors = s.building.filter((f) => f.room !== null);
          if (roomFloors.length === 0) return {};
          const idx = Math.floor(Math.random() * roomFloors.length);
          const targetLevel = roomFloors[idx].level;
          return {
            building: s.building.map((f) =>
              f.level === targetLevel && f.room
                ? {
                    ...f,
                    room: {
                      ...f.room,
                      condition: Math.max(0, f.room.condition - 30),
                    },
                  }
                : f,
            ),
          };
        },
        resultKey: 'evt_maintenance_accident_r1',
      },
    ],
    defense: {
      condition: (s) =>
        (s.ghostStaff || []).some((st) => st.type === 'repairman'),
      defenseKey: 'evt_maintenance_accident_defense',
      hintKey: 'evt_maintenance_accident_hint',
      reducedEffect: (s) => {
        // Only -10 condition instead of -30
        const roomFloors = s.building.filter((f) => f.room !== null);
        if (roomFloors.length === 0) return {};
        const idx = Math.floor(Math.random() * roomFloors.length);
        const targetLevel = roomFloors[idx].level;
        return {
          building: s.building.map((f) =>
            f.level === targetLevel && f.room
              ? {
                  ...f,
                  room: {
                    ...f.room,
                    condition: Math.max(0, f.room.condition - 10),
                  },
                }
              : f,
          ),
        };
      },
      bonusReward: 0,
    },
  },
  {
    id: 'sns_viral',
    tier: 2,
    nameKey: 'evt_sns_viral_name',
    descKey: 'evt_sns_viral_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_sns_viral_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 25, 0, 100),
          sp: s.sp + 500,
        }),
        resultKey: 'evt_sns_viral_r1',
      },
    ],
  },
  {
    id: 'total_blackout',
    tier: 2,
    nameKey: 'evt_total_blackout_name',
    descKey: 'evt_total_blackout_desc',
    condition: (s) =>
      (s.reputation || 50) >= 30 &&
      (s.lobbyWaitQueue || []).length >= 5,
    duration: 15000,
    autoResolve: 1,
    choices: [
      {
        labelKey: 'evt_total_blackout_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 8, 0, 100),
          sp: s.sp + 200,
        }),
        resultKey: 'evt_total_blackout_r1',
      },
      {
        labelKey: 'evt_total_blackout_c2',
        effect: () => ({}),
        resultKey: 'evt_total_blackout_r2',
      },
    ],
    defense: {
      condition: (s) => {
        const hasRepairman = (s.ghostStaff || []).some(
          (st) => st.type === 'repairman',
        );
        const totalLv = (s.lobbyBuilding || []).reduce(
          (sum, f) => sum + (f.facilityLevel || 0),
          0,
        );
        return hasRepairman && totalLv >= 15;
      },
      defenseKey: 'evt_total_blackout_defense',
      hintKey: 'evt_total_blackout_hint',
      reducedEffect: (s) => ({
        reputation: clamp((s.reputation || 50) + 8, 0, 100),
        sp: s.sp + 200,
      }),
      bonusReward: 100,
    },
  },
];

// ── Tier 3: Late-game (rep 70+, star 4+) ───────────────────

const tier3Events: GameEvent[] = [
  {
    id: 'famous_youtuber',
    tier: 3,
    nameKey: 'evt_famous_youtuber_name',
    descKey: 'evt_famous_youtuber_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_famous_youtuber_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 30, 0, 100),
          sp: s.sp + 2000,
        }),
        resultKey: 'evt_famous_youtuber_r1',
      },
    ],
  },
  {
    id: 'celeb_visit',
    tier: 3,
    nameKey: 'evt_celeb_visit_name',
    descKey: 'evt_celeb_visit_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_celeb_visit_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 40, 0, 100),
          sp: s.sp + 5000,
        }),
        resultKey: 'evt_celeb_visit_r1',
      },
    ],
  },
  {
    id: 'franchise_offer',
    tier: 3,
    nameKey: 'evt_franchise_offer_name',
    descKey: 'evt_franchise_offer_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_franchise_offer_c1',
        effect: (s) => ({ sp: s.sp + 10000 }),
        resultKey: 'evt_franchise_offer_r1',
      },
      {
        labelKey: 'evt_franchise_offer_c2',
        effect: () => ({}),
        resultKey: 'evt_franchise_offer_r2',
      },
    ],
  },
  {
    id: 'intl_contest',
    tier: 3,
    nameKey: 'evt_intl_contest_name',
    descKey: 'evt_intl_contest_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_intl_contest_c1',
        effect: (s) => ({
          sp: s.sp + 3000,
          reputation: clamp((s.reputation || 50) + 20, 0, 100),
        }),
        resultKey: 'evt_intl_contest_r1',
      },
    ],
  },
  {
    id: 'documentary',
    tier: 3,
    nameKey: 'evt_documentary_name',
    descKey: 'evt_documentary_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_documentary_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 50, 0, 100),
          sp: s.sp + 2000,
        }),
        resultKey: 'evt_documentary_r1',
      },
    ],
  },
  {
    id: 'ghost_king',
    tier: 3,
    nameKey: 'evt_ghost_king_name',
    descKey: 'evt_ghost_king_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_ghost_king_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 15, 0, 100),
          sp: s.sp + 1000,
        }),
        resultKey: 'evt_ghost_king_r1',
      },
    ],
  },
  {
    id: 'gov_subsidy',
    tier: 3,
    nameKey: 'evt_gov_subsidy_name',
    descKey: 'evt_gov_subsidy_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_gov_subsidy_c1',
        effect: (s) => ({ sp: s.sp + 5000 }),
        resultKey: 'evt_gov_subsidy_r1',
      },
    ],
  },
  {
    id: 'vip_party',
    tier: 3,
    nameKey: 'evt_vip_party_name',
    descKey: 'evt_vip_party_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_vip_party_c1',
        effect: (s) => ({ sp: s.sp + 8000 }),
        resultKey: 'evt_vip_party_r1',
      },
      {
        labelKey: 'evt_vip_party_c2',
        effect: () => ({}),
        resultKey: 'evt_vip_party_r2',
      },
    ],
    defense: {
      condition: (s) =>
        (s.lobbyBuilding || []).some(
          (f) => f.facilityType === 'costume_rental',
        ) &&
        (s.lobbyBuilding || []).some((f) => f.facilityType === 'photo_zone'),
      defenseKey: 'evt_vip_party_defense',
      hintKey: 'evt_vip_party_hint',
      reducedEffect: (s) => ({ sp: s.sp + 8000 }),
      bonusReward: 500,
    },
  },
  {
    id: 'horror_festival',
    tier: 3,
    nameKey: 'evt_horror_festival_name',
    descKey: 'evt_horror_festival_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_horror_festival_c1',
        effect: (s) => ({
          sp: s.sp + 5000,
          reputation: clamp((s.reputation || 50) + 30, 0, 100),
        }),
        resultKey: 'evt_horror_festival_r1',
      },
    ],
  },
  {
    id: 'legendary_review',
    tier: 3,
    nameKey: 'evt_legendary_review_name',
    descKey: 'evt_legendary_review_desc',
    condition: (s) =>
      (s.reputation || 50) >= 70 && (s.starLevel || 0) >= 4,
    duration: 0,
    choices: [
      {
        labelKey: 'evt_legendary_review_c1',
        effect: (s) => ({
          reputation: clamp((s.reputation || 50) + 20, 0, 100),
          fame: (s.fame || 0) + 100,
        }),
        resultKey: 'evt_legendary_review_r1',
      },
    ],
  },
];

// ── All Events ─────────────────────────────────────────────

export const ALL_EVENTS: GameEvent[] = [
  ...tier1Events,
  ...tier2Events,
  ...tier3Events,
];

export function getEventById(id: string): GameEvent | undefined {
  return ALL_EVENTS.find((e) => e.id === id);
}

/**
 * Pick eligible events from the pool,
 * excluding recent history.
 */
export function getEligibleEvents(
  state: GameStateData,
  history: string[],
): GameEvent[] {
  return ALL_EVENTS.filter(
    (e) => !history.includes(e.id) && e.condition(state),
  );
}
